import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SessionManager from '../utils/sessionManager';

export const useSessionTimeout = (onSessionExpired) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const navigate = useNavigate();

  const handleSessionExpired = useCallback(() => {
    SessionManager.clearSession();
    if (onSessionExpired) {
      onSessionExpired();
    }
    navigate('/login');
  }, [onSessionExpired, navigate]);

  const extendSession = useCallback(() => {
    SessionManager.updateLastActivity();
    setShowWarning(false);
  }, []);

  useEffect(() => {
    // Only set up session management if user is authenticated
    if (!SessionManager.isAuthenticated()) {
      return;
    }

    if (SessionManager.isSessionExpired()) {
      handleSessionExpired();
      return;
    }

    // Add activity listeners
    const activities = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const updateActivity = () => {
      if (SessionManager.isSessionExpired()) {
        handleSessionExpired();
        return;
      }

      SessionManager.updateLastActivity();
      setShowWarning(false);
    };

    activities.forEach(activity => {
      document.addEventListener(activity, updateActivity, true);
    });

    // Set up interval to check session timeout
    const sessionCheckInterval = setInterval(() => {
      if (!SessionManager.isAuthenticated()) {
        return;
      }

      if (SessionManager.isSessionExpired()) {
        handleSessionExpired();
        return;
      }

      if (SessionManager.shouldShowWarning()) {
        setShowWarning(true);
        setTimeRemaining(SessionManager.getTimeUntilExpiry());
      } else {
        setShowWarning(false);
      }
    }, SessionManager.ACTIVITY_CHECK_INTERVAL);

    // Cleanup function
    return () => {
      activities.forEach(activity => {
        document.removeEventListener(activity, updateActivity, true);
      });
      clearInterval(sessionCheckInterval);
    };
  }, [handleSessionExpired]);

  return {
    showWarning,
    timeRemaining,
    extendSession,
    formatTimeRemaining: SessionManager.formatTimeRemaining
  };
};

export default useSessionTimeout;